// src/data/allowlist.js
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db } from "./firebase";
import { auth } from "./auth";

/**
 * Read allowlist state for a UID from Firestore: /users/{uid}
 * Returns: { allowlisted, active, role, reason, error? }
 */
export async function isAllowlisted(uid) {
  if (!uid) return { allowlisted: false, reason: "no-uid" };
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return { allowlisted: false, reason: "no-doc" };

    const data = snap.data() || {};
    const active = !!data.active;
    const role = typeof data.role === "string" ? data.role : null;
    const allowlisted = active && !!role;

    return {
      allowlisted,
      active,
      role,               // "viewer" | "editor" | null
      reason: allowlisted ? null : (!active ? "inactive" : "no-role"),
    };
  } catch (error) {
    return { allowlisted: false, reason: "error", error };
  }
}

/**
 * Ensures current user is allowlisted.
 * - If not, (optionally) sign out and (optionally) redirect with uid/email query params.
 * Options:
 *   - emailFallback: string (used if auth.currentUser.email is empty)
 *   - signOutOnBlock: boolean (default true)
 *   - redirect: string | null (default null). e.g. "/access-restricted"
 *
 * Returns: { allowlisted, active, role, reason }
 */
export async function ensureAllowlistedOrAct({
  emailFallback,
  signOutOnBlock = true,
  redirect = null,      // set to "/access-restricted" if you add that page
} = {}) {
  const u = auth.currentUser;
  if (!u) return { allowlisted: false, reason: "not-signed-in" };

  const res = await isAllowlisted(u.uid);
  if (res.allowlisted) return res;

  try {
    if (signOutOnBlock) await signOut(auth);
  } catch {}

  if (redirect) {
    const q = new URLSearchParams({
      uid: u.uid,
      email: u.email || emailFallback || "",
      reason: res.reason || "",
    }).toString();
    window.location.replace(`${redirect}?${q}`);
  }

  return res;
}
